/**
 * `<countdown-timer>` as a Lit element in TypeScript with decorators (the course's
 * flagship TS example). Drop-in for the hand-rolled JS version: same tag, same
 * `to`/`end` attributes, same three `data-state` values, same single leak-free timer.
 *
 * The instant maths is timezone-agnostic: `to`/`end` carry the conference offset, so
 * `Date` resolves them to absolute instants and the remaining duration is correct for
 * every viewer.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

type TimerState = "upcoming" | "live" | "ended";

@customElement("countdown-timer")
export class CountdownTimer extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
    }
    .countdown {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2, 0.5rem);
      padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
      border: 1px solid var(--color-border, #d4d4d8);
      border-radius: var(--radius-md, 0.5rem);
      background-color: var(--color-surface-2, #f4f4f5);
      color: var(--color-text, #18181b);
      font-size: var(--text-sm, 0.875rem);
    }
    .dot {
      inline-size: 0.55rem;
      block-size: 0.55rem;
      border-radius: var(--radius-full, 9999px);
      background-color: var(--color-muted, #71717a);
      flex: none;
    }
    :host([data-state="live"]) .dot {
      background-color: #16a34a;
      animation: pulse 1.6s ease-in-out infinite;
    }
    :host([data-state="upcoming"]) .dot {
      background-color: var(--color-accent, #6366f1);
    }
    .label {
      color: var(--color-muted, #71717a);
    }
    .value {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      color: var(--color-text, #18181b);
    }
    @media (prefers-reduced-motion: reduce) {
      .dot {
        animation: none;
      }
    }
    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.35;
      }
    }
  `;

  /** ISO datetime to count down to (the session start). */
  @property() to = "";

  /** Optional ISO datetime the session ends; drives the "ended" state. */
  @property() end = "";

  // Plain fields (NOT `accessor`): the legacy `experimentalDecorators` path uses field
  // decorators; `accessor` is a standard-decorators-only feature.
  @state() private label = "";
  @state() private value = "";
  // `datetime` for the `<time>` element; empty omits the attribute (see `|| nothing`).
  @state() private datetime = "";

  #timer: ReturnType<typeof setInterval> | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  /** Restart the ticking timer when the inputs change (not on internal state). */
  override willUpdate(changed: Map<PropertyKey, unknown>) {
    if (this.isConnected && (changed.has("to") || changed.has("end"))) this.#start();
  }

  override render() {
    return html`
      <span class="countdown" role="timer" aria-live="off">
        <span class="dot" aria-hidden="true"></span>
        <span class="label">${this.label}</span>
        <time class="value" datetime=${this.datetime || nothing}>${this.value}</time>
      </span>
    `;
  }

  // The interval is assigned BEFORE the first tick so a terminal/invalid state (which
  // calls `#stop()` from within `#tick`) actually clears it.
  #start() {
    this.#stop();
    this.#timer = setInterval(() => this.#tick(), 1000);
    this.#tick();
  }

  // Stop and forget the timer (the leak-prevention seam).
  #stop() {
    if (this.#timer !== null) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #tick() {
    const startMs = Date.parse(this.to);
    if (Number.isNaN(startMs)) {
      this.#set("upcoming", "Time to be announced", "", "");
      this.#stop();
      return;
    }

    const now = Date.now();
    const endMs = this.end ? Date.parse(this.end) : Number.NaN;
    const hasEnd = !Number.isNaN(endMs);

    if (hasEnd && now >= endMs) {
      this.#set("ended", "Session ended", "", new Date(endMs).toISOString());
      this.#stop(); // Nothing more will change.
      return;
    }

    if (now >= startMs) {
      this.#set("live", "Happening now", "", new Date(startMs).toISOString());
      if (!hasEnd) this.#stop(); // No end boundary to wait for.
      return;
    }

    const remainingMs = startMs - now;
    this.#set("upcoming", "Starts in", formatRemaining(remainingMs), durationIso(remainingMs));
  }

  /** Reflect one computed state: `data-state` on the host (for CSS) plus the `@state`
   * fields Lit renders. */
  #set(timerState: TimerState, label: string, value: string, datetime: string) {
    this.dataset.state = timerState;
    this.label = label;
    this.value = value;
    this.datetime = datetime;
  }
}

/** Format a remaining duration as "{Nd }HH:MM:SS" (days only when non-zero). */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const clock = `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

/** ISO 8601 duration for `<time datetime>` (machine-readable remaining time). */
function durationIso(ms: number): string {
  return `PT${Math.max(0, Math.floor(ms / 1000))}S`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Drop-in for the JS factory so existing call sites keep working. With the tag-map
 * augmentation below, `createElement` is already typed (no cast needed).
 */
export function createCountdownTimer(): CountdownTimer {
  return document.createElement("countdown-timer");
}

declare global {
  interface HTMLElementTagNameMap {
    "countdown-timer": CountdownTimer;
  }
}
